import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { productCreateFromBillingAction } from "../../../../redux/actions/product-action";
import {
  taxCatListAction,
  manufacturesListAction,
  schListAction,
} from "../../../../redux/actions/general-action";
import { uomDropDownAction } from "../../../../redux/actions/settings-action";
import ItemsSelectFieldStandalone from "../../../forms/stand-alone-select-field";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import moment from "moment";

const formInputCls =
  "w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0";

const initialFormState = {
  item_name: "",
  uom: "",
  batch_number: "",
  exp_date: "",
  loose_qty: "0",
  pack_qty: "0",
  pack_size: "0",
  hsn: "",
  mrp: "",
  manufacturer: "",
  schedule: "",
  tax_category: "",
  discount_percentage: "",
};

// Build UOM dropdown options from raw API list (same shape as vendor-item-mapping / UOM modal)
const buildUomOptions = (rawList) =>
  (rawList || []).map((item) => ({
    label: item?.alt_uom || item?.uom_name || item?.label || "",
    value: item?.item_id ?? item?.uom_id ?? item?.value ?? "",
  })).filter((o) => o.value !== "" && o.value != null);

const VMProductSidePanel = ({ open, setOpen, selectedVendorItemId, onProductCreated, uomOptionsFromParent }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const [taxList, setTaxList] = useState([]);
  const [uomList, setUomList] = useState([]);
  const [manufacturesList, setManufacturesList] = useState([]);
  const [schList, setSchList] = useState([]);

  // Use parent's UOM options when provided (already loaded on vendor-item-mapping page)
  const uomOptions = Array.isArray(uomOptionsFromParent) && uomOptionsFromParent.length > 0
    ? buildUomOptions(uomOptionsFromParent)
    : uomList;

  useEffect(() => {
    dispatch(taxCatListAction((data) => setTaxList((data?.data || []).map((item) => ({ label: item?.code, value: item?.id }))), () => setTaxList([])));
    dispatch(uomDropDownAction(null, (res) => {
      const list = res?.data ?? (Array.isArray(res) ? res : []);
      setUomList(buildUomOptions(list));
    }, () => setUomList([])));
    dispatch(manufacturesListAction(
      (data) => setManufacturesList((data?.data && Array.isArray(data.data)) ? data.data.map((item) => ({ label: item?.name, value: item?.id })) : []),
      () => setManufacturesList([])
    ));
    dispatch(schListAction(
      (data) => setSchList((data?.data?.scheduleList || []).map((item) => ({ label: item?.schedule, value: item?.schedule }))),
      () => setSchList([])
    ));
  }, [dispatch]);

  useEffect(() => {
    if (open) {
      setForm(initialFormState);
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const validate = () => {
    const next = {};
    if (!form.item_name?.trim()) next.item_name = "Item Name is required";
    if (!form.batch_number?.trim()) next.batch_number = "Batch Number is required";
    if (!form.loose_qty?.trim()) next.loose_qty = "Loose Quantity is required";
    if (!form.uom) next.uom = "UOM is required";
    if (!form.tax_category) next.tax_category = "Tax Category is required";
    if (!form.hsn?.trim()) next.hsn = "HSN is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (form.exp_date && moment(form.exp_date).isBefore(moment().startOf("day"))) {
      errorToast("Cannot save products with expired dates. Please select a future expiration date.");
      return;
    }

    setIsSubmitting(true);
    const packQty = parseFloat(form.pack_qty || 0);
    const looseQty = parseFloat(form.loose_qty || 0);
    const qty = packQty + looseQty;
    const mrp = parseFloat(form.mrp) || 0;

    const apiPayload = {
      item_name: form.item_name,
      item_generic_name: form.item_name,
      item_uom_id: form.uom,
      item_tax_category: form.tax_category,
      item_unit_price: mrp,
      item_batch_number: form.batch_number,
      item_quantity: qty,
      item_discount_percentage: parseFloat(form.discount_percentage) || 0,
      item_total_amount: mrp * qty,
      item_pack_qty: packQty,
      item_loose_qty: looseQty,
      item_exp_date: form.exp_date || "",
      item_uom: "",
      item_hsn: form.hsn,
      item_manufacturer: form.manufacturer || "",
      item_schedule: form.schedule || "",
      item_pack_size: parseFloat(form.pack_size) || 0,
    };

    const onSuccess = (response) => {
      setIsSubmitting(false);
      successToast(response?.message || "Product created successfully");
      const data = response?.data || {};
      const item = {
        item_id: data.item_id,
        item_code: data.item_code || "",
        item_name: data.item_name || form.item_name,
      };
      if (onProductCreated) onProductCreated(item);
      setOpen(false);
    };

    const onError = (err) => {
      setIsSubmitting(false);
      errorToast(err?.data?.message || "Failed to create product");
    };

    dispatch(productCreateFromBillingAction(apiPayload, onSuccess, onError));
  };

  const handleCancel = () => {
    setForm(initialFormState);
    setErrors({});
    setOpen(false);
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-[#00000060] z-40" onClick={handleCancel} aria-hidden="true" />
      <div
        className="fixed right-0 top-0 h-full max-w-2xl bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 translate-x-0"
        style={{ "--primary": "#3B82F6", "--danger": "#EF4444" }}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add New Product</h2>
            <p className="text-sm text-gray-600 mt-1">Enter the below details to add new product</p>
          </div>
          <button type="button" onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto h-[82vh]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-F51D00">*</span></label>
              <input
                type="text"
                value={form.item_name}
                onChange={(e) => setField("item_name", e.target.value)}
                placeholder="Placeholder"
                className={formInputCls}
              />
              {errors.item_name && <p className="text-F51D00 text-xs mt-1">{errors.item_name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UOM <span className="text-F51D00">*</span></label>
                <ItemsSelectFieldStandalone
                  placeholder="Select UOM"
                  options={uomOptions}
                  isSearchable
                  value={form.uom}
                  onChange={(opt) => setField("uom", opt?.value ?? "")}
                />
                {errors.uom && <p className="text-F51D00 text-xs mt-1">{errors.uom}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loose Qty <span className="text-F51D00">*</span></label>
                <input
                  type="text"
                  value={form.loose_qty}
                  onChange={(e) => setField("loose_qty", e.target.value)}
                  placeholder="0"
                  className={formInputCls}
                />
                {errors.loose_qty && <p className="text-F51D00 text-xs mt-1">{errors.loose_qty}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch No. <span className="text-F51D00">*</span></label>
                <input
                  type="text"
                  value={form.batch_number}
                  onChange={(e) => setField("batch_number", e.target.value)}
                  placeholder="Placeholder"
                  className={formInputCls}
                />
                {errors.batch_number && <p className="text-F51D00 text-xs mt-1">{errors.batch_number}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exp Date</label>
                <input
                  type="date"
                  value={form.exp_date}
                  onChange={(e) => setField("exp_date", e.target.value)}
                  className={formInputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HSN <span className="text-F51D00">*</span></label>
                <input
                  type="text"
                  value={form.hsn}
                  onChange={(e) => setField("hsn", e.target.value)}
                  placeholder="Placeholder"
                  className={formInputCls}
                />
                {errors.hsn && <p className="text-F51D00 text-xs mt-1">{errors.hsn}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MRP</label>
                <input
                  type="text"
                  value={form.mrp}
                  onChange={(e) => setField("mrp", e.target.value)}
                  placeholder="Placeholder"
                  className={formInputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                <ItemsSelectFieldStandalone
                  placeholder="Select Manufacturer"
                  options={manufacturesList}
                  isSearchable
                  value={form.manufacturer}
                  onChange={(opt) => setField("manufacturer", opt?.value || "")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                <ItemsSelectFieldStandalone
                  placeholder="Select Schedule"
                  options={schList}
                  isSearchable
                  value={form.schedule}
                  onChange={(opt) => setField("schedule", opt?.value || "")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount%</label>
                <input
                  type="text"
                  value={form.discount_percentage}
                  onChange={(e) => setField("discount_percentage", e.target.value)}
                  placeholder="0"
                  className={formInputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Category <span className="text-F51D00">*</span></label>
                <ItemsSelectFieldStandalone
                  placeholder="Select Tax Category"
                  options={taxList}
                  isSearchable
                  value={form.tax_category}
                  onChange={(opt) => setField("tax_category", opt?.value || "")}
                />
                {errors.tax_category && <p className="text-F51D00 text-xs mt-1">{errors.tax_category}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pack Size</label>
              <input
                type="text"
                value={form.pack_size}
                onChange={(e) => setField("pack_size", e.target.value)}
                placeholder="0"
                className={formInputCls}
              />
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-200 bg-white">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2 rounded text-white text-[13px] font-semibold"
              style={{ backgroundColor: "var(--danger)" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 rounded text-white text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: isSubmitting ? "#9CA3AF" : "var(--primary)" }}
            >
              {isSubmitting ? "Creating..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VMProductSidePanel;
